import { Link } from 'react-router-dom';
import { ArrowRight, FileText, UserCheck, CalendarClock, Repeat, Laptop, Briefcase } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1763568258143-904ea924ac53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxJVCUyMHRlY2hub2xvZ3klMjBzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGxhcHRvcCUyMGNvZGluZ3xlbnwwfHx8fDE3NzI0NDAyNjR8MA&ixlib=rb-4.1.0&q=85';
const IT_IMG = 'https://images.pexels.com/photos/8636592/pexels-photo-8636592.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const staffingTypes = [
  { icon: CalendarClock, title: 'Contract Staffing', desc: 'Flexible short-term and long-term contract engagements to meet project-specific or seasonal demands with pre-vetted IT professionals.' },
  { icon: Repeat, title: 'Contract-to-Hire', desc: 'Evaluate candidates on the job before making a permanent commitment. Reduce hiring risk while ensuring cultural and technical fit.' },
  { icon: Briefcase, title: 'Permanent Hiring', desc: 'Full-cycle direct placement services for critical permanent roles. We source, screen, and present top-tier candidates aligned with your vision.' },
  { icon: Laptop, title: 'Project-Based Hiring', desc: 'Assemble dedicated project teams with the right skill mix for time-bound initiatives, product launches, and transformation programs.' },
  { icon: FileText, title: 'Remote Offshore Support', desc: 'Access skilled offshore IT professionals for round-the-clock coverage, cost optimization, and accelerated delivery timelines.' },
];

const industries = ['Banking & Financial Services', 'Healthcare & Life Sciences', 'Technology & Software', 'Retail & E-Commerce', 'Manufacturing & Logistics', 'Government & Public Sector', 'Telecommunications', 'Energy & Utilities'];

const ITStaffing = () => {
  return (
    <div data-testid="it-staffing-page">
      <PageHeader caption="Services" title="IT Staffing" subtitle="Specialized technology talent for every engagement model." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={IT_IMG} alt="IT professionals collaborating" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <p className="sol-caption mb-4">IT Talent Solutions</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Technology Talent That Delivers Results</h2>
              <p className="text-[#525252] leading-relaxed mb-4">Solnity's IT Staffing practice provides enterprise clients with access to highly skilled technology professionals across all major platforms, frameworks, and domains.</p>
              <p className="text-[#525252] leading-relaxed mb-8">Whether you need a single senior developer or an entire project team, our rigorous vetting process ensures every candidate meets the highest standards of technical proficiency and professional conduct.</p>
              <Link to="/hire-talent" className="sol-btn-primary text-sm">Start Hiring <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Engagement Models</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Flexible Staffing Solutions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {staffingTypes.map((s) => (
              <div key={s.title} data-testid={`it-card-${s.title.toLowerCase().replace(/[\s-]+/g, '-')}`} className="sol-service-card bg-white group">
                <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] mb-5 group-hover:border-black group-hover:bg-black transition-all duration-300">
                  <s.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="sol-caption mb-4">Industries</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Industry Expertise</h2>
              <p className="text-[#525252] leading-relaxed">Our IT staffing solutions span across multiple industries. We understand the unique technology requirements and regulatory landscapes of each sector.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {industries.map((ind) => (
                <div key={ind} className="flex items-center gap-3 p-4 border border-[#E5E5E5] hover:border-[#333333] transition-colors">
                  <div className="w-2 h-2 bg-black shrink-0" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{ind}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Build Your Technology Team</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">Let us help you find the IT professionals who will drive your next project forward.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/hire-talent" data-testid="it-cta-hire" className="sol-btn-inverse text-sm">Hire Talent <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/our-process" className="sol-btn-inverse text-sm">View Process <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ITStaffing;
